import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/DriverHolidays.jsx
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}
function localYmd(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
export default function DriverHolidays() {
  const navigate = useNavigate();

  // 기본값: 올해, 지난 날짜 포함
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [driverKeyword, setDriverKeyword] = useState("");
  const [showPastDates, setShowPastDates] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const yearRange = useMemo(
    () => ({ from: `${year}-01-01`, to: `${year}-12-31` }),
    [year]
  );

  const fetchList = async () => {
    setLoading(true);
    try {
      const company = localStorage.getItem("email");
      if (!company) {
        setRows([]);
        setLoading(false);
        return;
      }

      const todayYmd = localYmd();
      const visibleFrom =
        showPastDates || yearRange.from > todayYmd ? yearRange.from : todayYmd;

      // 선택 연도와 겹치는 휴무 (from <= 연말 AND to >= 연초/오늘)
      let query = supabase
        .from("holidays_driver")
        .select("id, driver, from, to, name, status")
        .eq("company", company)
        .lte("from", yearRange.to)
        .gte("to", visibleFrom)
        .order("from", { ascending: true });

      if (driverKeyword) {
        query = query.ilike("driver", `%${driverKeyword}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      console.error("기사 휴일 조회 실패:", e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (row, action) => {
    if (!row?.id || approvingId) return;

    const nextStatus = action === "approve" ? localYmd() : "비승인";
    const company = localStorage.getItem("email");
    setApprovingId(row.id);
    try {
      const { error } = await supabase
        .from("holidays_driver")
        .update({ status: nextStatus })
        .eq("id", row.id)
        .eq("company", company);

      if (error) throw error;

      setRows((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, status: nextStatus } : item
        )
      );
    } catch (e) {
      showUserAlert("승인여부 처리 실패: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setApprovingId(null);
      setConfirmAction(null);
    }
  };

  const openStatusConfirm = (row, action) => {
    if (!row?.id || approvingId) return;
    setConfirmAction({ row, action });
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 최초 1회

  const years = Array.from({ length: 7 }, (_, i) => today.getFullYear() - 3 + i); // 현재 기준 ±3년

  return (
    <div className="space-y-1">
      <style>{`
        .driver-holidays-table th,
        .driver-holidays-table td {
          text-align: center;
        }

        .driver-holidays-table .col-seq {
          width: 52px;
          min-width: 52px;
          white-space: nowrap;
        }

        .driver-holidays-table .col-driver {
          width: 86px;
          min-width: 86px;
          max-width: 86px;
          white-space: nowrap;
        }

        .driver-holidays-table .col-holiday-date {
          width: 180px;
          min-width: 180px;
          white-space: nowrap;
        }
      `}</style>
      <Header />

      <div className="px-4 pt-1 pb-4 space-y-4">
        <h2 className="text-lg font-semibold">기사 휴일</h2>

        {/* 검색 영역 */}
        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">휴무년도</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      className="border rounded px-2 py-1"
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">기사명</label>
                  <Input
                    value={driverKeyword}
                    onChange={(e) => setDriverKeyword(e.target.value)}
                    placeholder="예: 홍길동"
                    className="w-48"
                  />
                </div>

                <div>
                  <Button onClick={fetchList} disabled={loading}>
                    {loading ? "검색중..." : "검색"}
                  </Button>
                </div>

                <label className="flex h-10 cursor-pointer items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={showPastDates}
                    onChange={(e) => setShowPastDates(e.target.checked)}
                    className="h-4 w-4"
                  />
                  지난 날짜 보기
                </label>
              </div>
              <div className="text-xs text-gray-500">
                검색 범위: {yearRange.from} ~ {yearRange.to}
                {!showPastDates && ` (오늘 ${localYmd()} 이후)`}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 테이블 */}
        <Card>
          <CardContent className="bg-white">
            <div className="overflow-x-auto">
              <table className="driver-holidays-table table-auto w-full border text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {["순번", "기사명", "휴무일 시작", "휴무일 끝", "구분", "승인여부"].map((h) => (
                      <th
                        key={h}
                        className={
                          "border p-2 " +
                          (h === "순번" ? "col-seq " : "") +
                          (h === "기사명" ? "col-driver " : "") +
                          (["휴무일 시작", "휴무일 끝"].includes(h) ? "col-holiday-date " : "")
                        }
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td className="border p-3 text-center" colSpan={6}>
                        데이터가 없습니다.
                      </td>
                    </tr>
                  )}
                  {rows.map((r, idx) => (
                    <tr key={r.id || `${r.driver}-${idx}`}>
                      <td className="border p-2 col-seq">{idx + 1}</td>
                      <td className="border p-2 col-driver">{r.driver}</td>
                      <td className="border p-2 col-holiday-date">{r.from}</td>
                      <td className="border p-2 col-holiday-date">{r.to}</td>
                      <td className="border p-2">{r.name}</td>
                      <td className="border p-2">
                        {r.status && r.status !== "요청" ? (
                          r.status
                        ) : (
                          <div className="flex justify-center gap-2">
                            <Button
                              onClick={() => openStatusConfirm(r, "approve")}
                              disabled={approvingId === r.id}
                              className="h-7 px-3 text-xs"
                            >
                              {approvingId === r.id ? "처리중" : "승인"}
                            </Button>
                            <Button
                              onClick={() => openStatusConfirm(r, "reject")}
                              disabled={approvingId === r.id}
                              className="h-7 bg-red-600 px-3 text-xs text-white hover:bg-red-700"
                            >
                              비승인
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* 하단 등록 버튼 */}
        <div className="text-right">
          <Button onClick={() => navigate("/driver-holidays/new")}>등록</Button>
        </div>
      </div>
      {confirmAction && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4 no-print">
          <div className="w-full max-w-sm rounded bg-white p-5 shadow-lg">
            <p className="mb-5 text-center font-semibold">
              {confirmAction.action === "approve" ? "승인 하시겠습니까?" : "비승인 하시겠습니까?"}
            </p>
            <div className="flex justify-center gap-3">
              <Button
                type="button"
                onClick={() => handleStatusChange(confirmAction.row, confirmAction.action)}
                disabled={approvingId === confirmAction.row.id}
              >
                예
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmAction(null)}
                disabled={approvingId === confirmAction.row.id}
              >
                아니오
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
